import { supabase } from '../lib/supabaseClient';

export type GrowthRolloutStage =
  | 'foundation'
  | 'shadow'
  | 'read_only'
  | 'campaign'
  | 'automation'
  | 'general';

export type GrowthAccessReason =
  | 'allowed'
  | 'salon_required'
  | 'not_authorized'
  | 'account_inactive'
  | 'growth_entitlement_missing'
  | 'pilot_not_enabled'
  | 'communication_policy_missing'
  | 'observation_policy_missing'
  | 'access_check_failed';

export interface GrowthAccessResolution {
  canViewGrowth: boolean;
  canActInGrowth: boolean;
  showGrowthInNavigation: boolean;
  canViewGrowthAdvancedDetails: boolean;
  rolloutStage: GrowthRolloutStage | null;
  entitlementSource: string | null;
  communicationPolicyId: string | null;
  communicationPolicyVersion: number | null;
  observationPolicyId: string | null;
  observationPolicyVersion: number | null;
  reasonCode: GrowthAccessReason;
}

interface GrowthAccessRow {
  can_view_growth?: boolean | null;
  can_act_in_growth?: boolean | null;
  show_growth_in_navigation?: boolean | null;
  can_view_growth_advanced_details?: boolean | null;
  rollout_stage?: GrowthRolloutStage | null;
  entitlement_source?: string | null;
  communication_policy_id?: string | null;
  communication_policy_version?: number | null;
  observation_policy_id?: string | null;
  observation_policy_version?: number | null;
  reason_code?: GrowthAccessReason | null;
}

const deniedGrowthAccess: GrowthAccessResolution = {
  canViewGrowth: false,
  canActInGrowth: false,
  showGrowthInNavigation: false,
  canViewGrowthAdvancedDetails: false,
  rolloutStage: null,
  entitlementSource: null,
  communicationPolicyId: null,
  communicationPolicyVersion: null,
  observationPolicyId: null,
  observationPolicyVersion: null,
  reasonCode: 'access_check_failed',
};

function mapGrowthAccess(row?: GrowthAccessRow | null): GrowthAccessResolution {
  if (!row) {
    return deniedGrowthAccess;
  }

  return {
    canViewGrowth: row.can_view_growth === true,
    canActInGrowth: row.can_act_in_growth === true,
    showGrowthInNavigation: row.show_growth_in_navigation === true,
    canViewGrowthAdvancedDetails: row.can_view_growth_advanced_details === true,
    rolloutStage: row.rollout_stage ?? null,
    entitlementSource: row.entitlement_source ?? null,
    communicationPolicyId: row.communication_policy_id ?? null,
    communicationPolicyVersion: row.communication_policy_version ?? null,
    observationPolicyId: row.observation_policy_id ?? null,
    observationPolicyVersion: row.observation_policy_version ?? null,
    reasonCode: row.reason_code ?? 'access_check_failed',
  };
}

export async function resolveGrowthAccess(
  salonId?: string | null,
): Promise<GrowthAccessResolution> {
  if (!salonId) {
    return { ...deniedGrowthAccess, reasonCode: 'salon_required' };
  }

  const { data, error } = await supabase.rpc('resolve_growth_access_v1', {
    p_salon_id: salonId,
  });

  if (error) {
    console.error('Error resolving Growth access:', error);
    return deniedGrowthAccess;
  }

  const row = (Array.isArray(data) ? data[0] : data) as GrowthAccessRow | null;
  return mapGrowthAccess(row);
}

export async function checkGrowthFeatureAccess(salonId?: string | null): Promise<boolean> {
  if (!salonId) {
    return false;
  }

  const { data, error } = await supabase.rpc('check_growth_feature_access', {
    p_salon_id: salonId,
  });

  if (error) {
    console.error('Error checking Growth feature access:', error);
    return false;
  }

  return data === true;
}

export type GrowthOpportunityType = 'capacity_risk' | 'return_risk' | 'birthday' | 'owner_campaign';
export type GrowthOpportunityEvaluationStatus = 'actionable' | 'monitoring' | 'insufficient_data';
export type GrowthOpportunityState =
  | 'detected'
  | 'evaluating'
  | 'recommended'
  | 'accepted'
  | 'executing'
  | 'completed'
  | 'dismissed'
  | 'snoozed'
  | 'expired'
  | 'superseded';

export interface GrowthOpportunitySummary {
  id: string;
  opportunity_type: GrowthOpportunityType;
  state: GrowthOpportunityState;
  evaluation_status: GrowthOpportunityEvaluationStatus;
  window_start: string;
  window_end: string;
  rank_score: number;
  confidence: number;
  owner_evidence: Record<string, unknown>;
  detected_at: string;
  expires_at: string;
}

export interface GrowthDashboard {
  generated_at: string;
  summary: {
    active_count: number;
    actionable_count: number;
    monitoring_count: number;
    insufficient_data_count: number;
  };
  opportunities: GrowthOpportunitySummary[];
  recent_wins: GrowthRecentWin[];
}

export interface GrowthRecentWin {
  id: string;
  subject_kind: 'campaign' | 'automation_run';
  mission: string;
  evaluation_status: 'verified';
  verified_booking_count: number;
  completed_booking_count: number;
  completed_revenue: number;
  verified_at: string;
}

export type GrowthOpportunityFilter = 'active' | 'monitoring' | 'history' | 'all';

export interface GrowthOpportunityList {
  total_count: number;
  has_more: boolean;
  items: GrowthOpportunitySummary[];
}

export interface GrowthOpportunityEvent {
  event_type: string;
  from_state: GrowthOpportunityState | null;
  to_state: GrowthOpportunityState | null;
  reason_code: string;
  created_at: string;
}

export interface GrowthRecommendation {
  id: string;
  version: number;
  status: 'current' | 'accepted';
  action_kind: 'campaign' | 'automation' | 'none';
  proposal: {
    mission: string;
    audience_rationale: string;
    channel: 'email';
    timing: {
      mode: 'within_opportunity_window';
      window_start: string;
      window_end: string;
      timezone_source: 'salon';
    };
    message_brief: string;
    offer: null;
    policy_constraints: {
      communication_policy_id: string;
      communication_policy_version: number;
      channel: 'email';
      offers_enabled: boolean;
    };
  };
  rationale: {
    observation: string;
    why_now: string;
    uncertainty: string;
  };
  expected_impact: {
    kind: 'directional' | 'not_estimated';
    is_estimate: true;
    value: number | null;
    basis?: string;
  };
  confidence: number;
  prepared_by: 'deterministic' | 'ai_assisted';
  locale: 'en' | 'cs' | 'ru';
  valid_from: string;
  valid_until: string;
  communication_policy_version: number;
}

export interface GrowthRecommendationPreparation {
  status: 'not_started' | 'ready' | 'unavailable';
  reason?: 'no_safe_fallback' | 'communication_policy_missing' | string;
  created_at?: string;
}

export interface GrowthOpportunityDetail extends GrowthOpportunitySummary {
  rank_components: Record<string, unknown>;
  expected_value: number | null;
  detector_policy_version: number;
  evaluated_at: string;
  last_observed_at: string;
  events: GrowthOpportunityEvent[];
  recommendation: GrowthRecommendation | null;
  recommendation_preparation: GrowthRecommendationPreparation;
}

export type GrowthCampaignStatus =
  | 'review'
  | 'approved_pending_coordination'
  | 'scheduled'
  | 'executing'
  | 'dismissed';
export type GrowthCampaignDeliveryMode = 'send_now' | 'scheduled';
export type GrowthCoordinationStatus = 'approved' | 'delayed' | 'blocked';

export interface GrowthCampaignOffer {
  promotion_id: string;
  name: string;
  code: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  currency?: string;
  starts_at: string;
  ends_at?: string;
  approved_by_owner: true;
}

export interface GrowthCampaign {
  id: string;
  opportunity_id: string;
  recommendation_id: string;
  status: GrowthCampaignStatus;
  review_version: number;
  mission: string;
  audience_count: number | null;
  audience_reason: string;
  channel: 'email';
  subject: string;
  preheader: string;
  body: string;
  cta_label: string;
  cta_url: string | null;
  delivery_mode: GrowthCampaignDeliveryMode;
  proposed_send_at: string | null;
  promotion_id: string | null;
  offer: GrowthCampaignOffer | null;
  expected_impact: GrowthRecommendation['expected_impact'];
  rationale: GrowthRecommendation['rationale'];
  confidence: number;
  locale: 'en' | 'cs' | 'ru';
  communication_policy_version: number;
  offers_enabled: boolean;
  has_prior_approval: boolean;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
  execution_status:
    | 'not_started'
    | 'awaiting_coordination'
    | 'draft'
    | 'launching'
    | 'scheduled'
    | 'sending'
    | 'sent'
    | 'paused'
    | 'cancelled'
    | 'failed'
    | 'archived';
  execution_created: boolean;
}

export interface GrowthCampaignCoordination {
  coordination_attempt: number;
  status: GrowthCoordinationStatus;
  audience_count: number;
  approved_count: number;
  delayed_count: number;
  skipped_count: number;
  blocked_count: number;
  reason_counts: Record<string, number>;
  final_scheduled_at: string | null;
  execution_created: boolean;
  final_delivery_validation_required: true;
  created_at: string;
}

export interface GrowthCampaignCoordinationResult {
  campaign_id: string;
  coordination_attempt: number;
  coordination_status: GrowthCoordinationStatus;
  growth_status?: GrowthCampaignStatus;
  reason_code?: string | null;
  audience_count: number;
  approved_count: number;
  delayed_count: number;
  skipped_count: number;
  blocked_count?: number;
  queued_count?: number;
  final_scheduled_at?: string | null;
  execution_created: boolean;
  dry_run?: boolean;
  idempotent_replay: boolean;
  final_delivery_validation_required?: true;
}

export interface GrowthCampaignCommandResult {
  campaign_id: string;
  status: GrowthCampaignStatus;
  review_version?: number;
  idempotent_replay?: boolean;
  approval_required?: boolean;
  execution_created?: false;
  opportunity_id?: string;
  recommendation_id?: string;
  source_kind?: 'owner_initiated';
}

export type GrowthAutomationMission =
  | 'return_client'
  | 'birthday'
  | 'first_visit_follow_up'
  | 'new_service_announcement';
export type GrowthAutomationStatus =
  | 'suggested'
  | 'activated'
  | 'learning'
  | 'optimising'
  | 'paused'
  | 'archived';

export interface GrowthAutomationRunSummary {
  id: string;
  status: 'coordinating' | 'approved' | 'delayed' | 'skipped' | 'blocked' | 'completed' | 'failed';
  requested_at: string;
  final_scheduled_at: string | null;
  candidate_count: number;
  approved_count: number;
  delayed_count: number;
  skipped_count: number;
  blocked_count: number;
  reason_counts?: Record<string, number>;
  finished_at: string | null;
}

export interface GrowthAutomationSummary {
  id: string;
  mission: GrowthAutomationMission;
  status: GrowthAutomationStatus;
  review_version: number;
  approved_review_version: number | null;
  audience_summary: string;
  activated_at: string | null;
  paused_at: string | null;
  last_run_at: string | null;
  created_at: string;
  latest_run: GrowthAutomationRunSummary | null;
}

export interface GrowthAutomationEvent {
  id: string;
  event_type: string;
  from_status: GrowthAutomationStatus | null;
  to_status: GrowthAutomationStatus;
  review_version: number;
  reason_code: string;
  actor_kind: 'user' | 'worker' | 'system';
  created_at: string;
}

export interface GrowthAutomation extends GrowthAutomationSummary {
  channel: 'email';
  subject: string;
  preheader: string;
  body: string;
  cta_label: string;
  policy_version: string;
  requires_approval: boolean;
  archived_at: string | null;
  updated_at: string;
  runs: GrowthAutomationRunSummary[];
  events: GrowthAutomationEvent[];
}

export interface GrowthAutomationCommandResult {
  automation_id: string;
  status: GrowthAutomationStatus;
  review_version?: number;
  requires_approval?: boolean;
  idempotent_replay?: boolean;
}

export type GrowthOutcomeStatus = 'observing' | 'verified' | 'inconclusive' | 'invalidated';
export type GrowthOutcomeSubjectKind = 'campaign' | 'automation_run';

export interface GrowthOutcomeSummary {
  id: string;
  subject_kind: GrowthOutcomeSubjectKind;
  mission: string;
  evaluation_status: GrowthOutcomeStatus;
  measurement_started_at: string;
  measurement_ends_at: string;
  verified_booking_count: number;
  verified_booked_value: number;
  completed_booking_count: number;
  completed_revenue: number;
  observation_policy_version: number;
  verified_at: string | null;
  last_evaluated_at: string | null;
  created_at: string;
}

export interface GrowthOutcomeList {
  total_count: number;
  has_more: boolean;
  items: GrowthOutcomeSummary[];
}

export interface GrowthOutcomeEvent {
  event_type: string;
  from_status: GrowthOutcomeStatus | null;
  to_status: GrowthOutcomeStatus;
  evidence_revision: number;
  reason_code: string;
  created_at: string;
}

export interface GrowthOutcome extends GrowthOutcomeSummary {
  opportunity_id: string | null;
  growth_campaign_id: string | null;
  growth_automation_id: string | null;
  growth_automation_run_id: string | null;
  observation_policy: {
    id: string;
    version: number;
    measurement_key: 'capacity_campaign' | 'return_visit' | 'birthday';
    attribution_standard: 'verified_direct_or_promotion';
    revenue_standard: 'completed_validated';
  };
  expected_snapshot: Record<string, unknown>;
  learning_summary: {
    eligible_for_learning?: boolean;
    verified_booking_count?: number;
    completed_booking_count?: number;
    completed_revenue?: number;
  };
  invalidated_at: string | null;
  evidence_summary: {
    valid_count: number;
    invalidated_count: number;
    direct_count: number;
    promotion_count: number;
  };
  events: GrowthOutcomeEvent[];
}

export type GrowthActivityKind = 'campaign' | 'automation';
export type GrowthActivitySourceKind = 'growth' | 'previous_marketing';

export interface GrowthActivitySummary {
  id: string;
  activity_kind: GrowthActivityKind;
  source_kind: GrowthActivitySourceKind;
  title: string;
  status: string;
  occurred_at: string;
  legacy_resource_id: string | null;
  has_growth_lineage: boolean;
  detail_path: string;
}

export interface GrowthActivityList {
  total_count: number;
  has_more: boolean;
  items: GrowthActivitySummary[];
}

export interface GrowthLegacyRouteResolution {
  resolution:
    | 'linked_growth_campaign'
    | 'linked_growth_automation'
    | 'previous_activity';
  target_path: string | null;
}

export interface GrowthRolloutExitStatus {
  rollout_enabled: boolean;
  rollout_stage: GrowthRolloutStage | null;
  rollback_available: boolean;
  rollback_control: 'growth_pilot_access.status';
  retained_delivery_guard_present: boolean;
  incomplete_campaign_decisions: number;
  active_linked_legacy_automations: number;
  incomplete_automation_runs: number;
  ready: boolean;
}

export async function getGrowthDashboard(salonId: string): Promise<GrowthDashboard> {
  const { data, error } = await supabase.rpc('get_growth_dashboard_v1', {
    p_salon_id: salonId,
  });

  if (error) throw error;
  return data as unknown as GrowthDashboard;
}

export async function listGrowthOpportunities(
  salonId: string,
  options: { filter?: GrowthOpportunityFilter; limit?: number; offset?: number } = {},
): Promise<GrowthOpportunityList> {
  const { data, error } = await supabase.rpc('list_growth_opportunities_v1', {
    p_salon_id: salonId,
    p_filter: options.filter ?? 'active',
    p_limit: options.limit ?? 25,
    p_offset: options.offset ?? 0,
  });

  if (error) throw error;
  return data as unknown as GrowthOpportunityList;
}

export async function getGrowthOpportunity(
  salonId: string,
  opportunityId: string,
): Promise<GrowthOpportunityDetail> {
  const { data, error } = await supabase.rpc('get_growth_opportunity_v1', {
    p_salon_id: salonId,
    p_opportunity_id: opportunityId,
  });

  if (error) throw error;
  return data as unknown as GrowthOpportunityDetail;
}

export async function getGrowthCampaign(salonId: string, campaignId: string): Promise<GrowthCampaign> {
  const { data, error } = await supabase.rpc('get_growth_campaign_v1', {
    p_salon_id: salonId,
    p_campaign_id: campaignId,
  });

  if (error) throw error;
  return data as unknown as GrowthCampaign;
}

export async function getGrowthCampaignCoordination(
  salonId: string,
  campaignId: string,
): Promise<GrowthCampaignCoordination | null> {
  const { data, error } = await supabase.rpc('get_growth_campaign_coordination_v1', {
    p_salon_id: salonId,
    p_campaign_id: campaignId,
  });

  if (error) throw error;
  return data as unknown as GrowthCampaignCoordination | null;
}

export async function coordinateGrowthCampaign(
  salonId: string,
  campaignId: string,
  execute = true,
): Promise<GrowthCampaignCoordinationResult> {
  const { data, error } = await supabase.rpc('coordinate_growth_campaign_v1', {
    p_salon_id: salonId,
    p_campaign_id: campaignId,
    p_execute: execute,
    p_public_base_url: null,
  });

  if (error) throw error;
  return data as unknown as GrowthCampaignCoordinationResult;
}

export async function getGrowthCampaignForOpportunity(
  salonId: string,
  opportunityId: string,
): Promise<{ campaign_id: string; status: GrowthCampaignStatus; review_version: number } | null> {
  const { data, error } = await supabase.rpc('get_growth_campaign_for_opportunity_v1', {
    p_salon_id: salonId,
    p_opportunity_id: opportunityId,
  });

  if (error) throw error;
  return data as unknown as { campaign_id: string; status: GrowthCampaignStatus; review_version: number } | null;
}

export async function createGrowthCampaignReview(params: {
  salonId: string;
  recommendationId: string;
  deliveryMode: GrowthCampaignDeliveryMode;
  proposedSendAt?: string | null;
  idempotencyKey: string;
}): Promise<GrowthCampaignCommandResult> {
  const { data, error } = await supabase.rpc('create_growth_campaign_review_v1', {
    p_salon_id: params.salonId,
    p_recommendation_id: params.recommendationId,
    p_delivery_mode: params.deliveryMode,
    p_proposed_send_at: params.proposedSendAt ?? null,
    p_idempotency_key: params.idempotencyKey,
  });

  if (error) throw error;
  return data as unknown as GrowthCampaignCommandResult;
}

export async function updateGrowthCampaignReview(params: {
  salonId: string;
  campaignId: string;
  expectedReviewVersion: number;
  subject: string;
  preheader: string;
  body: string;
  ctaLabel: string;
  deliveryMode: GrowthCampaignDeliveryMode;
  proposedSendAt?: string | null;
  promotionId?: string | null;
}): Promise<GrowthCampaignCommandResult> {
  const { data, error } = await supabase.rpc('update_growth_campaign_review_v1', {
    p_salon_id: params.salonId,
    p_campaign_id: params.campaignId,
    p_expected_review_version: params.expectedReviewVersion,
    p_subject: params.subject,
    p_preheader: params.preheader,
    p_body: params.body,
    p_cta_label: params.ctaLabel,
    p_delivery_mode: params.deliveryMode,
    p_proposed_send_at: params.proposedSendAt ?? null,
    p_promotion_id: params.promotionId ?? null,
  });

  if (error) throw error;
  return data as unknown as GrowthCampaignCommandResult;
}

export async function approveGrowthCampaign(
  salonId: string,
  campaignId: string,
  expectedReviewVersion: number,
): Promise<GrowthCampaignCommandResult> {
  const { data, error } = await supabase.rpc('approve_growth_campaign_v1', {
    p_salon_id: salonId,
    p_campaign_id: campaignId,
    p_expected_review_version: expectedReviewVersion,
  });

  if (error) throw error;
  return data as unknown as GrowthCampaignCommandResult;
}

export async function dismissGrowthCampaign(
  salonId: string,
  campaignId: string,
): Promise<GrowthCampaignCommandResult> {
  const { data, error } = await supabase.rpc('dismiss_growth_campaign_v1', {
    p_salon_id: salonId,
    p_campaign_id: campaignId,
    p_reason: 'owner_dismissed',
  });

  if (error) throw error;
  return data as unknown as GrowthCampaignCommandResult;
}

export async function createOwnerInitiatedGrowthCampaign(params: {
  salonId: string;
  goalKind: 'introduce_service' | 'announce_availability' | 'welcome_clients_back';
  goalDetail: string;
  locale: 'en' | 'cs' | 'ru';
  deliveryMode: GrowthCampaignDeliveryMode;
  proposedSendAt?: string | null;
  idempotencyKey: string;
}): Promise<GrowthCampaignCommandResult> {
  const { data, error } = await supabase.rpc('create_owner_initiated_growth_campaign_v1', {
    p_salon_id: params.salonId,
    p_goal_kind: params.goalKind,
    p_goal_detail: params.goalDetail,
    p_locale: params.locale,
    p_delivery_mode: params.deliveryMode,
    p_proposed_send_at: params.proposedSendAt ?? null,
    p_idempotency_key: params.idempotencyKey,
  });

  if (error) throw error;
  return data as unknown as GrowthCampaignCommandResult;
}

export async function listGrowthAutomations(salonId: string): Promise<GrowthAutomationSummary[]> {
  const { data, error } = await supabase.rpc('list_growth_automations_v1', {
    p_salon_id: salonId,
  });

  if (error) throw error;
  return data as unknown as GrowthAutomationSummary[];
}

export async function listGrowthActivity(
  salonId: string,
  options: { kind?: GrowthActivityKind | 'all'; limit?: number; offset?: number } = {},
): Promise<GrowthActivityList> {
  const { data, error } = await supabase.rpc('list_growth_activity_v1', {
    p_salon_id: salonId,
    p_kind: options.kind ?? 'all',
    p_limit: options.limit ?? 25,
    p_offset: options.offset ?? 0,
  });

  if (error) throw error;
  return data as unknown as GrowthActivityList;
}

export async function resolveGrowthLegacyRoute(
  salonId: string,
  resourceKind: GrowthActivityKind,
  resourceId: string,
): Promise<GrowthLegacyRouteResolution> {
  const { data, error } = await supabase.rpc('resolve_growth_legacy_route_v1', {
    p_salon_id: salonId,
    p_resource_kind: resourceKind,
    p_resource_id: resourceId,
  });

  if (error) throw error;
  return data as unknown as GrowthLegacyRouteResolution;
}

export async function getGrowthRolloutExitStatus(
  salonId: string,
): Promise<GrowthRolloutExitStatus> {
  const { data, error } = await supabase.rpc('get_growth_rollout_exit_status_v1', {
    p_salon_id: salonId,
  });

  if (error) throw error;
  return data as unknown as GrowthRolloutExitStatus;
}

export async function getGrowthAutomation(
  salonId: string,
  automationId: string,
): Promise<GrowthAutomation> {
  const { data, error } = await supabase.rpc('get_growth_automation_v1', {
    p_salon_id: salonId,
    p_automation_id: automationId,
  });

  if (error) throw error;
  return data as unknown as GrowthAutomation;
}

export async function listGrowthOutcomes(
  salonId: string,
  options: { status?: GrowthOutcomeStatus | 'all'; limit?: number; offset?: number } = {},
): Promise<GrowthOutcomeList> {
  const { data, error } = await supabase.rpc('list_growth_outcomes_v1', {
    p_salon_id: salonId,
    p_status: options.status ?? 'all',
    p_limit: options.limit ?? 25,
    p_offset: options.offset ?? 0,
  });

  if (error) throw error;
  return data as unknown as GrowthOutcomeList;
}

export async function getGrowthOutcome(
  salonId: string,
  outcomeId: string,
): Promise<GrowthOutcome> {
  const { data, error } = await supabase.rpc('get_growth_outcome_v1', {
    p_salon_id: salonId,
    p_outcome_id: outcomeId,
  });

  if (error) throw error;
  return data as unknown as GrowthOutcome;
}

export async function createGrowthAutomation(params: {
  salonId: string;
  mission: GrowthAutomationMission;
  locale: 'en' | 'cs' | 'ru';
  idempotencyKey: string;
}): Promise<GrowthAutomationCommandResult> {
  const { data, error } = await supabase.rpc('create_growth_automation_v1', {
    p_salon_id: params.salonId,
    p_mission: params.mission,
    p_locale: params.locale,
    p_idempotency_key: params.idempotencyKey,
  });

  if (error) throw error;
  return data as unknown as GrowthAutomationCommandResult;
}

export async function updateGrowthAutomationMaterial(params: {
  salonId: string;
  automationId: string;
  expectedReviewVersion: number;
  subject: string;
  preheader: string;
  body: string;
  ctaLabel: string;
}): Promise<GrowthAutomationCommandResult> {
  const { data, error } = await supabase.rpc('update_growth_automation_material_v1', {
    p_salon_id: params.salonId,
    p_automation_id: params.automationId,
    p_expected_review_version: params.expectedReviewVersion,
    p_subject: params.subject,
    p_preheader: params.preheader,
    p_body: params.body,
    p_cta_label: params.ctaLabel,
  });

  if (error) throw error;
  return data as unknown as GrowthAutomationCommandResult;
}

async function runGrowthAutomationLifecycleCommand(
  command: 'activate' | 'pause' | 'resume' | 'archive',
  salonId: string,
  automationId: string,
  expectedReviewVersion?: number,
): Promise<GrowthAutomationCommandResult> {
  const { data, error } = await supabase.rpc(`${command}_growth_automation_v1`, {
    p_salon_id: salonId,
    p_automation_id: automationId,
    ...(command === 'activate' ? { p_expected_review_version: expectedReviewVersion } : {}),
  });

  if (error) throw error;
  return data as unknown as GrowthAutomationCommandResult;
}

export function activateGrowthAutomation(
  salonId: string,
  automationId: string,
  expectedReviewVersion: number,
) {
  return runGrowthAutomationLifecycleCommand('activate', salonId, automationId, expectedReviewVersion);
}

export function pauseGrowthAutomation(salonId: string, automationId: string) {
  return runGrowthAutomationLifecycleCommand('pause', salonId, automationId);
}

export function resumeGrowthAutomation(salonId: string, automationId: string) {
  return runGrowthAutomationLifecycleCommand('resume', salonId, automationId);
}

export function archiveGrowthAutomation(salonId: string, automationId: string) {
  return runGrowthAutomationLifecycleCommand('archive', salonId, automationId);
}